/**
 * Profile change service for @ollaid/native-sso
 * Uses authenticated IAM endpoints for email / phone change with OTP confirmation.
 *
 * @version 2.7.9
 */

import { fetchWithTimeout, getAuthToken, getHeaders, getIamApiBaseUrl, ensureDeviceContext } from './api';

export type ProfileChangeKind = 'email' | 'phone';
export type ProfileChangeMethod = 'email' | 'phone';

export interface ProfileChangeRequestResponse {
  success: boolean;
  message?: string;
  request_id?: number;
  method?: ProfileChangeMethod;
  otp_dev?: string | null;
}

export interface ProfileChangeVerifyResponse {
  success: boolean;
  message?: string;
  step?: 'pending_new_otp' | 'completed' | string;
  user?: unknown;
}

function getBaseUrl() {
  return getIamApiBaseUrl();
}

async function getAuthHeaders() {
  const token = getAuthToken();
  await ensureDeviceContext();
  return getHeaders(token || undefined, false);
}

export const profileChangeService = {
  async requestEmailChange(newEmail: string, method: ProfileChangeMethod = 'phone'): Promise<ProfileChangeRequestResponse> {
    const headers = await getAuthHeaders();
    return fetchWithTimeout<ProfileChangeRequestResponse>(
      `${getBaseUrl()}/backoffice/profile/email/request`,
      {
        method: 'POST',
        headers,
        body: JSON.stringify({ new_email: newEmail, method }),
      },
      30000
    );
  },

  async requestPhoneChange(ccphone: string, phone: string, method: ProfileChangeMethod = 'email'): Promise<ProfileChangeRequestResponse> {
    const headers = await getAuthHeaders();
    return fetchWithTimeout<ProfileChangeRequestResponse>(
      `${getBaseUrl()}/backoffice/profile/phone/request`,
      {
        method: 'POST',
        headers,
        body: JSON.stringify({ ccphone, phone, method }),
      },
      30000
    );
  },

  async verifyOldOTP(requestId: number, otpCode: string): Promise<ProfileChangeVerifyResponse> {
    const headers = await getAuthHeaders();
    return fetchWithTimeout<ProfileChangeVerifyResponse>(
      `${getBaseUrl()}/backoffice/profile/change/verify-old`,
      {
        method: 'POST',
        headers,
        body: JSON.stringify({ request_id: requestId, otp_code: otpCode }),
      },
      30000
    );
  },

  async verifyNewOTP(requestId: number, otpCode: string): Promise<ProfileChangeVerifyResponse> {
    const headers = await getAuthHeaders();
    return fetchWithTimeout<ProfileChangeVerifyResponse>(
      `${getBaseUrl()}/backoffice/profile/change/verify-new`,
      {
        method: 'POST',
        headers,
        body: JSON.stringify({ request_id: requestId, otp_code: otpCode }),
      },
      30000
    );
  },

  async resendOTP(requestId: number): Promise<ProfileChangeRequestResponse> {
    const headers = await getAuthHeaders();
    return fetchWithTimeout<ProfileChangeRequestResponse>(
      `${getBaseUrl()}/backoffice/profile/change/resend-otp`,
      {
        method: 'POST',
        headers,
        body: JSON.stringify({ request_id: requestId }),
      },
      30000
    );
  },

  async switchMethod(requestId: number, method: ProfileChangeMethod): Promise<ProfileChangeRequestResponse> {
    const headers = await getAuthHeaders();
    return fetchWithTimeout<ProfileChangeRequestResponse>(
      `${getBaseUrl()}/backoffice/profile/change/switch-method`,
      {
        method: 'POST',
        headers,
        body: JSON.stringify({ request_id: requestId, method }),
      },
      30000
    );
  },
};

export default profileChangeService;
